import React, {useState, useCallback, useEffect} from 'react';
import PropTypes from 'prop-types';
import { MetadataContent } from '@jutro/legacy/uiconfig';
import { useTranslator } from '@jutro/locale';
import {
    FormattedDate,
    CurrencyValue,
    useModal
} from '@jutro/components';
import { Button } from '@jutro/legacy/components';
import { Link } from '@jutro/router';

import { DateUtil } from '@xengage/gw-portals-util-js';
import {
    DataTable,
    DisplayColumn,
    defaultColumnFilter,
} from '@jutro/legacy/datatable';
import _ from 'lodash';
import moment from 'moment';
import classNames from 'classnames/bind';
import appConfig from 'app-config';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import {MLModalService, MLTagManagerService} from 'ml-portals-services-react';
import {MLGatewayAccountBillingDetailsService} from "gw-capability-gateway-billing";

import metadata from './MLPolicyRecentPaymentsComponent.metadata.json5';
import messages from './MLPolicyRecentPaymentsComponent.messages';
import styles from './MLPolicyRecentPaymentsComponent.module.scss';
import {FormModalPopover, platformMessages} from "ml-components-platform-react";
import MLVoidPaymentPopup from '../MLVoidPaymentPopup/MLVoidPaymentPopup';
import MLVoidPaymentPopupMetadata from '../MLVoidPaymentPopup/MLVoidPaymentPopup.metadata.json5';
import {UserService} from "gw-capability-gateway";

const cx = classNames.bind(styles)
const TIMEFRAME_30_DAYS = '30days';
const TIMEFRAME_90_DAYS = '90days';
const TIMEFRAME_6_MONTHS = '6months';
const TIMEFRAME_1_YEAR = '1year';
const TIMEFRAME_3_YEARS = '3years';
const TIMEFRAME_5_YEARS = '5years';

const MLPolicyRecentPaymentsComponent = (props) => {
    const {
        billingSummary,
        formatAmountString,
        addAlert
    } = props;
    const translator = useTranslator();
    const [activeCards, setActiveCards] = useState(['']);
    const [recentPayments, setRecentPayments] = useState(billingSummary.recentPayments_Ext)
    const [ dayFilter, setDayFilter] = useState(TIMEFRAME_30_DAYS);
    const [filteredPayments, setFilteredPayments] = useState([]);
    const [currentUser, setCurrentUser] = useState({});
    const { authHeader } = useAuthentication();
    const { showErrorModal, showProgressDialog } = MLModalService();
    const { refreshData } = useDataRefresh();
    const { MLUserTypes } = appConfig;
    const modalApi = useModal();

    const goToSelectedAccordion = (thisCard) => {
        if (_.find(activeCards, (c) => c === thisCard)) {
            setActiveCards(['']);
        } else {
            setActiveCards([thisCard]);
        }
    };

    const isCardActive = (thisCard) => {
        return _.find(activeCards, (c) => c === thisCard);
    };

    const renderHeader = (active, card) => {
        return (
            <div className="row">
                <div className="col-md-6">
                    <h2 className="section-header vertical-spacing-20">{translator(messages.payments)}</h2>
                    <p>{translator(messages.paymentsMayOnlyBeVoided)}</p>
                </div>
                <div className="col-md-6">
                    <Button
                        className="pull-right"
                        type="outlined"
                        onClick={() => goToSelectedAccordion(`${card}`)}
                    >
                        {isCardActive(`${card}`) ? translator(messages.collapse) : translator(messages.viewAll)}
                    </Button>
                </div>
            </div>
        )
    };

    const renderEffectiveDate = (item) => {
        return moment(item.effectiveDate).format('MM/DD/YYYY');
    };

    const renderTransactionDate = (item) => {
        return moment(item.transactionDate).format('MM/DD/YYYY');
    };

    const renderTransactionType = (item) => {
        return item.transactionType;
    };

    const renderTransactionAmt = (item) => {
        return formatAmountString(item.transactionAmount);
    };

    const renderRemainingBalance = (item) => {
        return formatAmountString(item.remainingBalance);
    };

    const generateTableCellStyles = (hasGrid, isBold, isVoid) => ({
        headerClassName: styles['table-header'],
        cellClassName: cx('cell-background', 'cell-size', { 'cell-grid-left': hasGrid, 'cell-bold': isBold || isVoid, 'cell-void': isVoid})
    })

    const addDays = (date, daysToAdd) => {
        date.setDate(date.getDate() + daysToAdd);
        return date;
    };

    const setFilterDate = useCallback((option) => {
        let filterDate;
        const currentDate = DateUtil.currentDate();
        if (option === TIMEFRAME_30_DAYS) {
            filterDate = addDays(new Date(currentDate), -30);
        } else if (option === TIMEFRAME_90_DAYS) {
            filterDate = addDays(new Date(currentDate), -90);
        } else if (option === TIMEFRAME_6_MONTHS) {
            filterDate = new Date(new Date(currentDate).setMonth(new Date(currentDate).getMonth() - 6));
        } else if (option === TIMEFRAME_1_YEAR) {
            filterDate = new Date(new Date(currentDate).setFullYear(new Date(currentDate).getFullYear() - 1));
        } else if (option === TIMEFRAME_3_YEARS) {
            filterDate = new Date(new Date(currentDate).setFullYear(new Date(currentDate).getFullYear() - 3));
        } else if (option === TIMEFRAME_5_YEARS) {
            filterDate = new Date(new Date(currentDate).setFullYear(new Date(currentDate).getFullYear() - 5));
        }
        return filterDate;
    },[]);

    const filterPaymentsByDate = (payments, filterDate) => {
        if (!payments) {
            return [];
        }
        if (!filterDate) {
            return payments;
        }
        return payments.filter((pmt) => {
            const dateFilter = new Date(pmt.transactionDateTime)
            return dateFilter >= filterDate;
        });
    };

    const getPaymentInstrumentName = (payment) => {
        let instrument = payment.paymentMethod || payment.paymentInstrument;
        if (payment.paymentType === 'Agency Sweep') {
            instrument = 'ACH/EFT';
        }
        return payment.last4Digits ? `${instrument} (${payment.last4Digits})` : instrument;
    }

    const getPaymentStatus = (payment) => payment.hasBeenReversed ? 'Reversed' : payment.isVoidable ? 'Posted' : 'Processed';

    const getReversalReason = (payment) => payment.reversalReason || '';

    const generatePaymentInstrumentContent = useCallback((payment) => <div className={styles['cell-size']}>{getPaymentInstrumentName(payment)}</div>, []);

    const getPaymentChannel = (payment) => {
        return payment.paymentType || payment.paymentChannel
    }

    const generatePaymentStatusContent = useCallback((payment) => {
        if(payment.status === 'scheduled'){
            return (
                <div><span className={styles['cell-size']}>Scheduled</span></div>
            );
        }
        else if (payment.hasBeenReversed) {
            return (
                <div><span className={styles['cell-void']}>{getPaymentStatus(payment)}</span></div>
            );
        } else {
            return (
                <div><span className={styles['cell-size']}>{getPaymentStatus(payment)}</span></div>
            );
        }
    }, []);

    const generateReversalReasonContent = useCallback((payment) => <div className={styles['cell-size']}>{getReversalReason(payment)}</div>, []);

     const updateFilteredPaymentsByDate = useCallback((selectedValue) => {
        const filterDate = setFilterDate(selectedValue);
        const filterScheduledPayments = billingSummary.scheduledPayments_Ext.filter(pmt => pmt.paymentChannel.toLowerCase().includes("css") && pmt.status === 'scheduled');
        setFilteredPayments([
            ...filterScheduledPayments,
            ...filterPaymentsByDate(recentPayments, filterDate),
        ]);
        setDayFilter(selectedValue);
    },[recentPayments, setFilterDate]);

    const refreshPaymentsList = useCallback((policyNumber) => {
        const paymentsPromise = MLGatewayAccountBillingDetailsService.searchPaymentsForPolicy(policyNumber, authHeader);
        paymentsPromise.then((payments) => {
            setRecentPayments(payments);
            refreshData();
        });
    }, [authHeader, refreshData]);

    const onVoidPayment = useCallback((payment) => {
        const voidPayment = (pmt) => {
            const voidPromise = (pmt.isBundlePayment)
                ? MLGatewayAccountBillingDetailsService.voidBundledOneTimePayment(pmt.confirmationNumber, pmt.accountNumber, authHeader)
                : MLGatewayAccountBillingDetailsService.voidOneTimePayment(pmt.publicID, authHeader)
            showProgressDialog(platformMessages.processingOperation, voidPromise);

            voidPromise.then((response) => {
                if (response.errorMessage) {
                    if (response.voidStatus === 'Accepted') {
                        addAlert('voidPayment', 'success', messages.voidPaymentNotReversed);
                    } else if (currentUser.isIA) {
                        addAlert('voidPayment', 'error', messages.voidPaymentFailIA);
                    } else if (currentUser.isPCS) {
                        addAlert('voidPayment', 'error', messages.voidPaymentFailEA);
                    } else {
                        addAlert('voidPayment', 'error', messages.voidPaymentFailGCS);
                    }
                } else {
                    addAlert('voidPayment', 'success', messages.voidPaymentSuccessful);
                }
                refreshPaymentsList(pmt.policyNumber);
            });
        };

        const componentProps = {
            showHeader: false,
            uiProps: MLVoidPaymentPopupMetadata.componentContent,
            CustomComponent: MLVoidPaymentPopup,
            overrideProps: {
                isBundle: payment.isBundlePayment,
                confirmationNumber: payment.confirmationNumber
            }
        };

        modalApi.showModal(
            <FormModalPopover {...componentProps} />
        ).then(() => {
            voidPayment(payment);
        }).catch(() => {
            console.log('Modal CANCEL button clicked');
        });
    }, [authHeader, modalApi, refreshPaymentsList, showProgressDialog]);

    const generateActionColumn = useCallback((payment, index) => {
        return (
            <div className='text-align-left'>
                {payment.isVoidable && (
                    <span>
                        <Link id={`void${index}`} onClick={() => onVoidPayment(payment)}>{translator(messages.void)}</Link>
                    </span>
                )}
            </div>
        );
    }, [onVoidPayment, translator]);

    useEffect(() => {
        UserService.getCurrentUser(authHeader).then((user) => {
            setCurrentUser(user);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const filterDate = setFilterDate(dayFilter);
        const filterScheduledPayments = billingSummary.scheduledPayments_Ext.filter(pmt => pmt.paymentChannel.toLowerCase().includes("css") && pmt.status === 'scheduled');
        setFilteredPayments([
            ...filterScheduledPayments,
            ...filterPaymentsByDate(recentPayments, filterDate),
        ]);
    }, [dayFilter, recentPayments, setFilterDate]);

    const paymentHistoryTable = (paymentHistoryProps) => <DataTable {...paymentHistoryProps}>
        <DisplayColumn
            key="paymentDate"
            id="paymentDate"
            textAlign="left"
            header="Payment Date"
            path="paymentDate"
            {...generateTableCellStyles()}
            columnProportion={2}
            renderCell={(row) => <FormattedDate value={row.transactionDate || row.paymentDate} displayFormat="vshort" />}
            sortable={false}
        />
        <DisplayColumn
            key="transactionAmount"
            id="transactionAmount"
            textAlign="left"
            header="Amount"
            path="transactionAmount"
            {...generateTableCellStyles(true)}
            columnProportion={2}
            renderCell={(row) => <CurrencyValue value={row.paymentAmount || row.amount} className={styles['cell-size']} showFractions />}
            sortable={false}
        />
        <DisplayColumn
            key="paymentInstrument"
            id="paymentInstrument"
            textAlign="left"
            header="Payment Instrument"
            path="paymentMethod"
            {...generateTableCellStyles(true)}
            cell={generatePaymentInstrumentContent}
            columnProportion={3}
            sortable={false}
        />
        <DisplayColumn
            key="paymentChannel"
            id="paymentChannel"
            textAlign="left"
            header="Payment Channel"
            path="paymentType"
            cell={getPaymentChannel}
            {...generateTableCellStyles(true, false)}
            columnProportion={3}
            sortable={false}
        />
        <DisplayColumn
            key="confirmationNumber"
            id="confirmationNumber"
            textAlign="left"
            header="Confirmation Number"
            path="confirmationNumber"
            columnProportion={3}
            headerClassName={styles.header}
            {...generateTableCellStyles(true, false)}
            sortable={false}
        />
        <DisplayColumn
            key="status"
            id="status"
            textAlign="left"
            header="Status"
            path="hasBeenReversed"
            columnProportion={2}
            headerClassName={styles.header}
            {...generateTableCellStyles(true, false, false)}
            cell={generatePaymentStatusContent}
            sortable={false}
        />
        <DisplayColumn
            key="reversalReason"
            id="reversalReason"
            textAlign="left"
            header="Reversal Reason"
            path="reversalReason"
            columnProportion={3}
            headerClassName={styles.header}
            {...generateTableCellStyles(true, true, true)}
            cell={generateReversalReasonContent}
            sortable={false}
        />
        <DisplayColumn
            id="actions"
            key="actions"
            textAlign="left"
            columnProportion={2}
            header={translator(messages.actions)}
            headerClassName={styles.header}
            sortable={false}
            {...generateTableCellStyles(true, false)}
            cell={generateActionColumn}
        />
    </DataTable>

    const overrideProps = {
        accordionContainer: {
            accordionStates: activeCards
        },
        paymentHistoryCard: {
            renderHeader: renderHeader
        },
        paymentHistoryTable: {
            data: filteredPayments,
            showSearch: false,
            showPagination: false,
            columnsConfigurable: false,
            onColumnFilter: defaultColumnFilter,
        },
        paymentDaysDropdown: {
            value: dayFilter,
            onValueChange: (value) => updateFilteredPaymentsByDate(value)
        }
    }

    const resolvers = {
        resolveCallbackMap: {
            renderHeader,
            renderEffectiveDate,
            renderTransactionDate,
            renderTransactionType,
            renderTransactionAmt,
            renderRemainingBalance
        },
        resolveClassNameMap: styles,
        resolveComponentMap: {
            paymentHistoryTable,
        }
    };

    return (
        <MetadataContent
            uiProps={metadata.componentContent}
            overrideProps={overrideProps}
            {...resolvers} />
    );
};

MLPolicyRecentPaymentsComponent.propTypes = {
    billingSummary: PropTypes.shape({
        paymentBillingHistory_ML: PropTypes.array
    }),
    formatAmountString: PropTypes.func,
    addAlert: PropTypes.func
};

MLPolicyRecentPaymentsComponent.defaultProps = {
};

export default MLPolicyRecentPaymentsComponent;